/**
 * Builds the toast shown after a user marks themselves "Going" and is auto-joined into the
 * event's spaces. Pure (same response in, same toast out) so the authed Going button
 * (useEventGoingSave / EventDrawerActions) and the guest post-auth path (executePostAuthIntent)
 * share one source of toast copy and never drift. meetball voice leads with the space name over a
 * bare count (see .claude/skills/meetball-tone-of-voice/SKILL.md), so we surface the first space
 * and fold any extras into "and N more". A space that errors on join is reported too (the POST is
 * 201 even on partial failure) so the miss isn't silently swallowed.
 */
import type { ToastOptions } from '@hooks/use-toast';
import { readingTimeMs } from '@lib/toastDurations';
import type { AttendEventResponse, AttendEventSpaceResult, PinRequiredSpace } from '@models/api';
import { isPinRequiredSpace } from '@utils/eventPinCelebration';
import { formatSpaceNameLists } from '@utils/heldPostCopy';

// No exclamation mark: the Going PIN sheet's title says the same moment as "You're going".
const GOING_TITLE = "You're going";

/**
 * A bucket its readers can index into: an array whose every item is an object, since each reader
 * reads `space_id` or `space_name` off every item (TECH-718). A null (or undefined) item would
 * throw there, and any other item that is not an object (a number, a string) carries no Space to
 * read, so the bucket is not one to trust either.
 */
const isReadableBucket = (bucket: unknown): boolean =>
  Array.isArray(bucket) &&
  bucket.every((item: unknown) => typeof item === 'object' && item !== null);

/**
 * True when a 201's body is shaped like the attend summary its readers index into: all four
 * buckets present, each an array, and every item in them an object (`isReadableBucket`).
 *
 * Every consumer of an `AttendEventResponse` spreads or measures those buckets and reads their
 * items, so a truthy but malformed body (contract drift, a proxy's HTML error page, a partially
 * serialized response, a null item) throws a TypeError deep inside whichever callback is reading
 * it, and neither caller degrades gracefully:
 * - in the toggle pipeline the throw happens inside `onSuccess` / `onSettled`, both of which
 *   query-core awaits inside `Mutation#execute`'s try. A throw there re-runs the whole settle as
 *   the ERROR path, so an attend that actually landed rolls the optimistic flip back, shows a
 *   destructive toast on top of the success toast that already fired, and rejects `mutateAsync` -
 *   and the invalidation pass for the successful response never runs at all.
 * - in `executePostAuthIntent` it lands in the outer catch, which returns the fallback route
 *   having invalidated nothing at all, on the user's very first authenticated screen.
 *
 * Guarding turns the same input into the honest answer - "unreadable, so assume it may have
 * landed" - which sweeps wide instead of pretending the attend failed. Mirrors the
 * `results == null` guard the share pipeline already carries (its G6 case).
 *
 * It checks the shapes of the buckets and their items, not the names in them: a `space_name` a
 * toast cannot show (null, not a string, blank) is a missing name, and the toast counts that list
 * instead of naming it (`formatSpaceNameLists`), so a drifted name never throws and never needs
 * this guard.
 */
export function isReadableAttendSummary(response: unknown): response is AttendEventResponse {
  const summary = response as Partial<AttendEventResponse> | null | undefined;
  return (
    !!summary &&
    isReadableBucket(summary.joined) &&
    isReadableBucket(summary.pending) &&
    isReadableBucket(summary.skipped) &&
    isReadableBucket(summary.failed)
  );
}

/** The Going toasts name the first Space and count the rest: "AI Builders and 2 more". */
const GOING_LIST = { namedAtMost: 1 } as const;

const spaceNames = (spaces: readonly AttendEventSpaceResult[]): string[] =>
  spaces.map(space => space.space_name);

/**
 * "AI Builders", "AI Builders and 1 more", "AI Builders and 3 more". Name first, never a bare
 * count. The names are dressed as every sentence that names Spaces dresses them
 * (`formatSpaceNameLists`, TECH-718): a long one cut in the middle, each in its own isolate,
 * quoted when the sentence needs it, and Spaces that share a name counted ("2 Spaces called Berlin
 * Tech"). The lists one line names go in together (`lists`), so no name reads like another
 * across them and the line quotes all its names or none. Callers only name a non-empty list.
 */
const nameLists = (...lists: AttendEventSpaceResult[][]): string[] =>
  formatSpaceNameLists(
    lists.map(spaces => ({ ...GOING_LIST, names: spaceNames(spaces) })),
    { oneSentence: true },
  );

/**
 * The PIN spaces of a Going response, well-formed items only; empty when an older server omits
 * the field. A malformed item is dropped here, so it can never reach an `event_pin` intent (whose
 * validator would then drop the whole intent, sheet and ticket question included).
 */
export function pinRequiredOf(response: AttendEventResponse): PinRequiredSpace[] {
  const listed: unknown = response.pin_required;
  return Array.isArray(listed) ? listed.filter(isPinRequiredSpace) : [];
}

/**
 * A requested Space a membership reader can use. Looser than the sheet's guard
 * (`isPinRequiredSpace`) on purpose: a PIN Space the sheet cannot show is still a PENDING row the
 * caches must sweep.
 */
function isRequestedSpace(
  value: unknown,
): value is Pick<AttendEventSpaceResult, 'space_id' | 'space_name'> {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.space_id === 'string' && v.space_id !== '' && typeof v.space_name === 'string';
}

/**
 * The Spaces a Going left a join request in: `pending`, plus the PIN Spaces (`pin_required`).
 * Since TECH-663 the backend reports a PIN Space's request in its own bucket, but it is the same
 * PENDING row, so every reader that means "a request is waiting" reads both through this.
 */
export function requestedSpacesOf(
  response: AttendEventResponse,
): Pick<AttendEventSpaceResult, 'space_id' | 'space_name'>[] {
  const listed: unknown = response.pin_required;
  return [...response.pending, ...(Array.isArray(listed) ? listed.filter(isRequestedSpace) : [])];
}

/**
 * False when `pin_required` lists an item `requestedSpacesOf` had to drop. A request went to that
 * Space, but the response no longer names it, so a reader must not map the response to "provably
 * these Spaces and no others" (or to "nothing moved"): it widens to `unknown` instead.
 */
export function requestedSpacesAreComplete(response: AttendEventResponse): boolean {
  const listed: unknown = response.pin_required;
  return !Array.isArray(listed) || listed.every(isRequestedSpace);
}

/**
 * Every variant that names Spaces on a second line stays up as long as it takes to read
 * (`readingTimeMs`, at least 5 s), a success too; "You're going" alone keeps the app's 2 s
 * default. The rule: `@lib/toastDurations`.
 */
export function buildAttendToast(response: AttendEventResponse): ToastOptions {
  const { joined, pending, failed } = response;
  // The line names the Spaces it joined (or asked to join) and any it could not join: one
  // sentence, so its names are cut and quoted together (`nameLists`).
  const [leadList, failedList] = nameLists(joined.length > 0 ? joined : pending, failed);
  // A space that errored on join is appended so a partial failure isn't silently swallowed (the
  // POST is 201 even then). Parenthetical keeps the happy path (failed === []) byte-identical.
  const failedNote = failed.length > 0 ? ` (couldn't join ${failedList})` : '';
  const namingSpaces = (description: string) => ({
    title: GOING_TITLE,
    description,
    duration: readingTimeMs(GOING_TITLE, description),
  });

  // Joined at least one space: they're in. Lead with the space they joined.
  if (joined.length > 0) {
    return { ...namingSpaces(`Joined ${leadList}${failedNote}`), variant: 'success' };
  }

  // No open join, but a request went out to a gated space (admins will review it).
  if (pending.length > 0) {
    return { ...namingSpaces(`Request sent to ${leadList}${failedNote}`), variant: 'success' };
  }

  // Nothing joined or pending, but a space write errored. The RSVP still landed, so confirm it
  // and flag the miss as a warning, not a hard error (they ARE going).
  if (failed.length > 0) {
    return { ...namingSpaces(`Couldn't join ${failedList}. Try again.`), variant: 'warning' };
  }

  // Spaceless event, or already a member of everything: just confirm the RSVP. A title alone,
  // so no duration of its own.
  return { title: GOING_TITLE, variant: 'success' };
}

/**
 * The toast for a Going whose PIN sheet is up (TECH-663): only what the sheet cannot say. The
 * sheet answers for the PIN Spaces and confirms the Going itself, so `joined` and `pending` stay
 * hidden, but a Space whose write errored is on no card, so it still gets the warning. Null when
 * nothing failed.
 */
export function buildPinSheetFailedToast(response: AttendEventResponse): ToastOptions | null {
  if (response.failed.length === 0) return null;
  // The sheet already says "You're going": the toast says only what the sheet cannot, Space
  // first, in the words of every other join that could not be served. It lands while the sheet
  // enters (with confetti), so it stays up long enough to be read after (`readingTimeMs`).
  const [failedList] = nameLists(response.failed);
  const title = `Couldn't join ${failedList}`;
  const description = 'Try again in a moment.';
  return {
    title,
    description,
    variant: 'warning',
    duration: readingTimeMs(title, description),
  };
}
